import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { ShieldCheck, ExternalLink, Lock, Globe, AlertTriangle, CheckCircle2, Plug } from "lucide-react";

export default function GatewayPage() {
  const { formatApiError } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState({ gateway_url: "https://SelleCheck.transactiongateway.com" });
  const [form, setForm] = useState({
    gateway_username: "",
    gateway_merchant_id: "",
    gateway_environment: "production",
    api_key_last4: "",
    notes: "",
  });
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const load = async () => {
    setLoading(true);
    try {
      const [s, i] = await Promise.all([
        api.get("/gateway/status").then((r) => r.data),
        api.get("/gateway/info").then((r) => r.data),
      ]);
      setStatus(s); setInfo(i);
    } catch {
      toast.error("Could not load gateway status");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const connect = async (e) => {
    e.preventDefault();
    try {
      await api.post("/gateway/connect", form);
      toast.success("Gateway linked");
      setForm({ gateway_username: "", gateway_merchant_id: "", gateway_environment: "production", api_key_last4: "", notes: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const disconnect = async () => {
    try {
      await api.post("/gateway/disconnect");
      toast.success("Gateway unlinked");
      load();
    } catch { toast.error("Could not disconnect"); }
  };

  const launch = async () => {
    try {
      const { data } = await api.post("/gateway/launch");
      toast.success("Opening gateway in a new tab — sign in there with your gateway credentials.");
      window.open(data.url, "_blank", "noopener,noreferrer");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  if (loading) return <div className="text-slate-500 text-sm">Loading gateway status...</div>;

  const connected = status?.connected;

  return (
    <div data-testid="gateway-page">
      <p className="text-xs uppercase tracking-[0.18em] text-[#C8A046] font-semibold">PCI-Compliant Processing</p>
      <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight mt-2 text-slate-900">
        Transaction Gateway
      </h1>
      <p className="text-slate-600 mt-2 max-w-2xl">
        Card and eCheck transactions run through{" "}
        <a href={info.gateway_url} target="_blank" rel="noopener noreferrer" className="text-[#0E2148] font-semibold underline-offset-4 hover:underline">
          SelleCheck.transactiongateway.com
        </a>
        — a separate, PCI-DSS Level 1 environment with its own login. Cardholder data never touches this dashboard.
      </p>

      {/* PCI Notice */}
      <div className="mt-6 p-5 rounded-xl border border-[#0E2148]/15 bg-gradient-to-br from-[#0E2148]/5 via-white to-[#C8A046]/5 flex items-start gap-3" data-testid="gateway-pci-notice">
        <Lock className="w-5 h-5 text-[#0E2148] mt-0.5 shrink-0" />
        <div>
          <div className="font-medium text-slate-900">Why a separate login?</div>
          <p className="text-sm text-slate-600 mt-1">
            PCI-DSS forbids storing cardholder data outside the certified environment. Your gateway credentials
            (username + password) live only on the gateway. From here, you link your gateway account by username,
            then click <em>Launch Gateway</em> to authenticate directly with them.
          </p>
        </div>
      </div>

      {/* Status / Connect */}
      <div className="grid lg:grid-cols-2 gap-6 mt-8">
        <div className="sk-card p-6" data-testid="gateway-status-card">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Plug className="w-4 h-4 text-slate-500" />
              <span className="text-xs uppercase tracking-wider text-slate-500">Connection</span>
            </div>
            {connected ? (
              <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold uppercase tracking-wider bg-[#10B981]/15 text-[#10B981] border border-[#10B981]/30 inline-flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3" /> Linked
              </span>
            ) : (
              <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold uppercase tracking-wider bg-amber-100 text-amber-800 border border-amber-200 inline-flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" /> Not linked
              </span>
            )}
          </div>

          {connected ? (
            <>
              <dl className="mt-5 grid grid-cols-2 gap-4 text-sm">
                <FieldDl label="Gateway username" value={status.gateway_username} />
                <FieldDl label="Merchant ID" value={status.gateway_merchant_id || "—"} />
                <FieldDl label="Environment" value={(status.gateway_environment || "production").toUpperCase()} />
                <FieldDl label="API key" value={status.api_key_last4 ? `•••• ${status.api_key_last4}` : "Not provided"} />
                <FieldDl label="Linked at" value={fmtDate(status.connected_at)} />
                <FieldDl label="Last launched" value={fmtDate(status.last_launched_at) || "Never"} />
              </dl>
              <div className="mt-6 flex flex-wrap gap-3">
                <Button onClick={launch} className="bg-[#00D533] hover:bg-[#00E636] text-black font-semibold h-11" data-testid="gateway-launch-btn">
                  <ExternalLink className="w-4 h-4 mr-2" /> Launch Gateway
                </Button>
                <Button onClick={disconnect} variant="ghost" className="border border-slate-300 hover:bg-slate-100 h-11" data-testid="gateway-disconnect-btn">
                  Unlink
                </Button>
              </div>
              <p className="mt-4 text-xs text-slate-500">
                You'll be redirected to <span className="font-mono">SelleCheck.transactiongateway.com</span>. Sign in with the gateway password
                you set when your account was provisioned.
              </p>
            </>
          ) : (
            <p className="mt-5 text-sm text-slate-600">
              Link your gateway account using your gateway username. We won't ask for your gateway password.
              You'll authenticate directly with the gateway when launching.
            </p>
          )}
        </div>

        {!connected && (
          <form onSubmit={connect} className="sk-card p-6 space-y-4" data-testid="gateway-connect-form">
            <h3 className="font-heading text-lg text-slate-900 flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-[#0E2148]" /> Link your gateway account
            </h3>
            <div>
              <Label className="text-slate-700">Gateway username</Label>
              <Input required value={form.gateway_username} onChange={upd("gateway_username")}
                className="mt-2 bg-white border-slate-200 h-11" placeholder="e.g., acme_corp_5821"
                data-testid="gateway-username-input" />
              <p className="mt-1 text-xs text-slate-500">The username you use at <span className="font-mono">SelleCheck.transactiongateway.com</span>.</p>
            </div>
            <div>
              <Label className="text-slate-700">Gateway merchant ID</Label>
              <Input value={form.gateway_merchant_id} onChange={upd("gateway_merchant_id")}
                className="mt-2 bg-white border-slate-200 h-11" placeholder="Optional"
                data-testid="gateway-mid-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-slate-700">Environment</Label>
                <Select value={form.gateway_environment} onValueChange={(v) => setForm({ ...form, gateway_environment: v })}>
                  <SelectTrigger className="mt-2 bg-white border-slate-200 h-11" data-testid="gateway-env-trigger"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white border-slate-200 text-slate-900">
                    <SelectItem value="production">Production</SelectItem>
                    <SelectItem value="sandbox">Sandbox</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-700">API key — last 4</Label>
                <Input value={form.api_key_last4} onChange={upd("api_key_last4")}
                  maxLength={4}
                  placeholder="e.g., 9F3A"
                  className="mt-2 bg-white border-slate-200 h-11 font-mono"
                  data-testid="gateway-key-input" />
              </div>
            </div>
            <div>
              <Label className="text-slate-700">Notes (optional)</Label>
              <Textarea value={form.notes} onChange={upd("notes")}
                className="mt-2 bg-white border-slate-200 min-h-[80px]" placeholder="Internal note..."
                data-testid="gateway-notes-input" />
            </div>
            <div className="flex items-start gap-2 text-xs text-slate-500 pt-2 border-t border-slate-200">
              <Lock className="w-3.5 h-3.5 mt-0.5 text-slate-400 shrink-0" />
              <span>We never collect or store your gateway password, full API key, or any cardholder data. Your gateway is the system of record.</span>
            </div>
            <Button type="submit" className="bg-[#00D533] hover:bg-[#00E636] text-black font-semibold h-11" data-testid="gateway-connect-btn">
              Link gateway account
            </Button>
          </form>
        )}

        {connected && (
          <div className="sk-card p-6" data-testid="gateway-actions-card">
            <h3 className="font-heading text-lg text-slate-900">What you can do at the gateway</h3>
            <ul className="mt-4 space-y-3 text-sm text-slate-700">
              {[
                "Process card and eCheck/ACH transactions (one-off and recurring)",
                "Tokenize cards into the gateway's PCI vault",
                "Refunds, voids, and pre-auth captures",
                "Batch settlement reports & funding statements",
                "Customer profile & subscription management",
              ].map((t) => (
                <li key={t} className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-[#10B981] mt-0.5 shrink-0" /> {t}
                </li>
              ))}
            </ul>
            <div className="mt-6 flex items-center gap-2 text-xs text-slate-500">
              <Globe className="w-3.5 h-3.5" />
              <a href={info.gateway_url} target="_blank" rel="noopener noreferrer" className="font-mono hover:underline">
                {info.gateway_url.replace("https://", "")}
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function FieldDl({ label, value }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wider text-slate-500">{label}</dt>
      <dd className="mt-1 text-slate-900 font-mono text-sm break-all">{value || "—"}</dd>
    </div>
  );
}

function fmtDate(s) {
  if (!s) return null;
  try { return new Date(s).toLocaleString(); } catch { return s; }
}
